import { useState } from 'react';

import { useIntl } from 'react-intl';
import { Form, Col, Row } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { useFormikContext } from 'formik';
import { faker } from '@faker-js/faker/locale/en';

import { inviteWhite } from 'assets';
import { Input, Select } from 'components/Forms';
import { getGenderOptions } from 'constants/constants';
import { buildPatientLanguageOptions } from 'utils/Locale';

import { IInvitation } from '../Onboard';

interface PatientInfoProps {
  isMRNMandatory: boolean;
}

const EASTER_EGG_CLICK_THRESHOLD = 5;

const DemographicInformation = ({ isMRNMandatory }: PatientInfoProps) => {
  const intl = useIntl();
  const { values, setValues, setFieldValue, handleChange } = useFormikContext<IInvitation>();
  const [iconClickCount, setIconClickCount] = useState(0);

  const handleBmiChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e);
    setFieldValue('bmiChangedFlag', true);
  };

  const today = DateTime.now().toFormat('yyyy-MM-dd');

  const handleIconClick = () => {
    if (iconClickCount === EASTER_EGG_CLICK_THRESHOLD) {
      const firstName = faker.person.firstName();
      const lastName = faker.person.lastName();
      setValues(
        {
          ...values,
          patientInfo: {
            ...values.patientInfo,
            firstName,
            lastName,
            bday: faker.date
              .birthdate({ min: 18, max: 90, mode: 'age' })
              .toISOString()
              .split('T')[0],
            gender: faker.helpers.arrayElement(getGenderOptions().map((gender) => gender.value)),
            locale: faker.helpers.arrayElement(['en', 'es', 'fr']),
            username: `${firstName.toLocaleLowerCase()}${lastName.toLocaleLowerCase()}${faker.string.numeric({ length: { min: 1, max: 4 } })}`,
            email_address: `${lastName.toLocaleLowerCase()[0]}${firstName.toLocaleLowerCase()}${faker.string.numeric({ length: { min: 1, max: 4 } })}@yopmail.com`,
            mrn: `MRN-${faker.string.numeric({ length: 3 })}-${faker.string.numeric({ length: 5 })}`,
            mobileNumber: faker.phone.number({ style: 'national' }),
            phoneNumber: faker.phone.number({ style: 'national' }),
            ssn: faker.string.numeric({ length: 4 })
          }
        },
        true
      );
      setIconClickCount(0);
    } else {
      setIconClickCount((currentCount) => currentCount + 1);
    }
  };

  const handleSSNChange = (e: any) => {
    // block adding letters or characters past 4 numbers
    if ((isNaN(e.key) || e.target.value.length >= 4) && e.key !== 'Backspace') {
      e.preventDefault();
    }
  };

  return (
    <>
      <h4 className="mb-4" data-test="patient-info-title">
        {intl.formatMessage({ id: 'patientKey' })}
      </h4>
      <div
        className="d-flex align-items-center p-4 bg-blue-light mb-4 br-5"
        data-test="bulk-invite-disclaimer"
      >
        <img src={inviteWhite} onClick={handleIconClick} style={{ cursor: 'pointer' }} />
        <p className="m-0 ms-3">
          {intl.formatMessage({ id: 'onboardMultiplePatientsSupportKey' })}
        </p>
      </div>
      <Row>
        <Form.Group className="form-group" as={Col} md="6">
          <Input
            type="text"
            placeholder={intl.formatMessage({ id: 'firstName' })}
            label={intl.formatMessage({ id: 'firstName' })}
            name="patientInfo.firstName"
            required
            data-test="invite-patient-info-firstName"
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="6">
          <Input
            type="text"
            placeholder={intl.formatMessage({ id: 'lastName' })}
            label={intl.formatMessage({ id: 'lastName' })}
            name="patientInfo.lastName"
            required
            data-test="invite-patient-info-lastName"
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="6">
          <Input
            type="date"
            label={intl.formatMessage({ id: 'Birthdate' })}
            name="patientInfo.bday"
            required
            data-test="invite-patient-info-bday"
            onChange={handleBmiChange}
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="6">
          <Select
            placeholder={intl.formatMessage({ id: 'selectGenderKey' })}
            label={intl.formatMessage({ id: 'Gender' })}
            name="patientInfo.gender"
            options={getGenderOptions()}
            isSearchable={false}
            required
            dataTestId="invite-patient-info-gender"
            containerNoWrap
            onChange={() =>
              // Cannot do the same as handleBmiChange since handleChange prevent it to trigger
              setFieldValue('bmiChangedFlag', true)
            }
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="6">
          <Select
            placeholder={intl.formatMessage({ id: 'languageTitle' })}
            label={intl.formatMessage({ id: 'languageTitle' })}
            name="patientInfo.locale"
            options={buildPatientLanguageOptions()}
            isSearchable={false}
            required
            dataTestId="invite-patient-info-language"
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="6">
          <Input
            type="text"
            placeholder={intl.formatMessage({ id: 'usernameKey' })}
            label={`${intl.formatMessage({
              id: 'usernameKey'
            })} ${intl.formatMessage({ id: 'usernameInviteDisclaimerKey' })}`}
            name="patientInfo.username"
            required
            data-test="invite-patient-info-username"
            max={today}
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="6">
          <Input
            type="email"
            placeholder={intl.formatMessage({ id: 'emailKey' })}
            label={intl.formatMessage({ id: 'emailKey' })}
            name="patientInfo.email_address"
            data-test="invite-patient-info-email"
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="6">
          <Input
            type="text"
            placeholder={intl.formatMessage({ id: 'patientAlternateIdKey' })}
            label={intl.formatMessage({ id: 'alternateIdKey' })}
            name="patientInfo.mrn"
            required={isMRNMandatory}
            data-test="invite-patient-info-alternateId"
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="6">
          <Input
            type="phone"
            placeholder={intl.formatMessage({ id: 'examplePhoneNumberKey' })}
            label={intl.formatMessage({ id: 'mobileNumber' })}
            name="patientInfo.mobileNumber"
            data-test="invite-patient-info-mobileNumber"
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="6">
          <Input
            type="phone"
            placeholder={intl.formatMessage({ id: 'examplePhoneNumberKey' })}
            label={intl.formatMessage({ id: 'otherPhoneNumber' })}
            name="patientInfo.phoneNumber"
            data-test="invite-patient-info-phoneNumber"
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="6">
          <Input
            type="text"
            placeholder="XXXX"
            pattern="[0-9]{4}"
            onKeyPress={handleSSNChange}
            label={intl.formatMessage({ id: 'ssnFourDigitsKey' })}
            name="patientInfo.ssn"
            data-test="invite-patient-info-ssn"
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="6">
          <Input
            type="text"
            label={intl.formatMessage({ id: 'doctor_in_charge' })}
            name="medicalInfo.provider"
            data-test="invite-patient-info-provider"
          />
        </Form.Group>
      </Row>
    </>
  );
};

export default DemographicInformation;
